import type { Locale } from '../i18n';

export type DashboardStatusTone = 'success' | 'warning' | 'active';

interface DashboardContent {
  sidebar: {
    logo: string;
    menuItems: Array<{ label: string; icon: string; active: boolean }>;
    user: {
      name: string;
      avatar: string;
      role: string;
    };
  };
  page: {
    title: string;
    subtitle: string;
  };
  stats: Array<{
    label: string;
    value: string;
    change: string;
    trend: 'up' | 'down';
  }>;
  chart: {
    title: string;
    viewDetails: string;
    data: Array<{ label: string; value: number }>;
  };
  table: {
    title: string;
    headers: string[];
    rows: Array<{
      customer: string;
      project: string;
      amount: string;
      status: string;
      statusTone: DashboardStatusTone;
    }>;
  };
  quickActionsTitle: string;
  quickActions: Array<{ label: string; icon: string }>;
}

export const localizedDashboardContent: Record<Locale, DashboardContent> = {
  zh: {
    sidebar: {
      logo: 'NeuroAI',
      menuItems: [
        { label: '概览', icon: 'layout-dashboard', active: true },
        { label: '项目', icon: 'folder', active: false },
        { label: '分析', icon: 'bar-chart', active: false },
        { label: '成员', icon: 'users', active: false },
        { label: '设置', icon: 'settings', active: false },
      ],
      user: {
        name: 'Alex Chen',
        avatar: 'AC',
        role: '管理员',
      },
    },
    page: {
      title: '仪表盘',
      subtitle: '欢迎回来，这里是项目的最新状态。',
    },
    stats: [
      { label: '总收入', value: '$124,563', change: '+12.5%', trend: 'up' },
      { label: '活跃用户', value: '8,549', change: '+8.2%', trend: 'up' },
      { label: '转化率', value: '3.24%', change: '-0.4%', trend: 'down' },
      { label: '平均会话', value: '4分32秒', change: '+2.1%', trend: 'up' },
    ],
    chart: {
      title: '收入概览',
      viewDetails: '查看详情',
      data: [
        { label: '1月', value: 4200 },
        { label: '2月', value: 3800 },
        { label: '3月', value: 5100 },
        { label: '4月', value: 4600 },
        { label: '5月', value: 6200 },
        { label: '6月', value: 7500 },
      ],
    },
    table: {
      title: '最近交易',
      headers: ['客户', '项目', '金额', '状态'],
      rows: [
        { customer: 'Sarah Miller', project: '网页开发', amount: '$2,400', status: '已完成', statusTone: 'success' },
        { customer: 'James Wilson', project: 'UI 设计', amount: '$1,800', status: '进行中', statusTone: 'active' },
        { customer: 'Emily Brown', project: 'API 集成', amount: '$3,200', status: '待处理', statusTone: 'warning' },
        { customer: 'Michael Lee', project: '咨询服务', amount: '$950', status: '已完成', statusTone: 'success' },
      ],
    },
    quickActionsTitle: '快捷操作',
    quickActions: [
      { label: '新建项目', icon: 'plus' },
      { label: '添加成员', icon: 'user-plus' },
      { label: '生成报告', icon: 'file-text' },
      { label: '查看分析', icon: 'bar-chart' },
    ],
  },
  en: {
    sidebar: {
      logo: 'NeuroAI',
      menuItems: [
        { label: 'Dashboard', icon: 'layout-dashboard', active: true },
        { label: 'Projects', icon: 'folder', active: false },
        { label: 'Analytics', icon: 'bar-chart', active: false },
        { label: 'Users', icon: 'users', active: false },
        { label: 'Settings', icon: 'settings', active: false },
      ],
      user: {
        name: 'Alex Chen',
        avatar: 'AC',
        role: 'Admin',
      },
    },
    page: {
      title: 'Dashboard',
      subtitle: "Welcome back. Here's what's happening with your projects.",
    },
    stats: [
      { label: 'Total Revenue', value: '$124,563', change: '+12.5%', trend: 'up' },
      { label: 'Active Users', value: '8,549', change: '+8.2%', trend: 'up' },
      { label: 'Conversion Rate', value: '3.24%', change: '-0.4%', trend: 'down' },
      { label: 'Avg. Session', value: '4m 32s', change: '+2.1%', trend: 'up' },
    ],
    chart: {
      title: 'Revenue Overview',
      viewDetails: 'View Details',
      data: [
        { label: 'Jan', value: 4200 },
        { label: 'Feb', value: 3800 },
        { label: 'Mar', value: 5100 },
        { label: 'Apr', value: 4600 },
        { label: 'May', value: 6200 },
        { label: 'Jun', value: 7500 },
      ],
    },
    table: {
      title: 'Recent Transactions',
      headers: ['Customer', 'Project', 'Amount', 'Status'],
      rows: [
        { customer: 'Sarah Miller', project: 'Web Development', amount: '$2,400', status: 'Completed', statusTone: 'success' },
        { customer: 'James Wilson', project: 'UI Design', amount: '$1,800', status: 'In Progress', statusTone: 'active' },
        { customer: 'Emily Brown', project: 'API Integration', amount: '$3,200', status: 'Pending', statusTone: 'warning' },
        { customer: 'Michael Lee', project: 'Consulting', amount: '$950', status: 'Completed', statusTone: 'success' },
      ],
    },
    quickActionsTitle: 'Quick Actions',
    quickActions: [
      { label: 'New Project', icon: 'plus' },
      { label: 'Add User', icon: 'user-plus' },
      { label: 'Generate Report', icon: 'file-text' },
      { label: 'View Analytics', icon: 'bar-chart' },
    ],
  },
};
